import React from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate, useParams } from 'react-router-dom';
import { ArrowLeftIcon, MoreHorizontalIcon, PlusIcon, ChevronRightIcon, BuildingIcon, UserIcon, LandmarkIcon, SmartphoneIcon, CreditCardIcon, WalletIcon } from 'lucide-react';
import { getTenant } from '../../../theme/tokens';
import { mockSuppliers } from '../mocks/mockSuppliers';
export function SupplierDetail() {
  const {
    t
  } = useTranslation('suppliers');
  const navigate = useNavigate();
  const {
    id
  } = useParams();
  const supplier = mockSuppliers.find(s => s.id === id) || mockSuppliers[0];
  const fullAddress = [supplier.addressLine1, supplier.addressLine2, supplier.city, supplier.province, supplier.postcode, supplier.country].filter(Boolean).join(', ');
  const billingEmail = supplier.useAsBillingEmail ? supplier.email : `${supplier.email} (separate billing)`;
  const bills = {
    total: supplier.amountOwed !== `${getTenant().currency.code} 0.00` ? 1 : 0,
    due: supplier.amountOwed !== `${getTenant().currency.code} 0.00` ? 1 : 0,
    awaitingApproval: 0,
    scheduled: 0,
    paid: 0
  };
  const history = [{
    id: 'h1',
    action: `${supplier.name} was created`,
    date: supplier.dateCreated.replace(',', ' at')
  }];
  return <div className="space-y-8 max-w-7xl mx-auto pb-12" data-id="element-12225">
      {/* Header */}
      <div className="flex items-center" data-id="element-12226">
        <button onClick={() => navigate('/suppliers')} className="flex items-center text-gray-900 hover:text-gray-600 transition-colors text-lg font-semibold" data-id="element-12227">
          <ArrowLeftIcon size={20} className="mr-2" data-id="element-12228" />
          {t('pages.SupplierDetail.all_suppliers')}
        </button>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-8" data-id="element-12229">
        {/* Left Column - Main Details */}
        <div className="lg:col-span-2 space-y-6" data-id="element-12230">
          <div className="bg-white rounded-xl border border-gray-200 shadow-sm overflow-hidden" data-id="element-12231">
            {/* Top Section */}
            <div className="p-8 border-b border-gray-100" data-id="element-12232">
              <div className="flex justify-between items-start" data-id="element-12233">
                <div data-id="element-12234">
                  <h1 className="text-2xl font-bold text-gray-900 mb-2" data-id="element-12235">
                    {supplier.name}
                  </h1>
                  <div className="flex items-center text-sm" data-id="element-12236">
                    <span className="text-gray-500 mr-2" data-id="element-12237">
                      {t('pages.SupplierDetail.you_owe_them')}
                    </span>
                    <span className="px-2.5 py-0.5 bg-[#FFC70A] text-black text-xs font-semibold rounded-md shadow-sm" data-id="element-12238">
                      {supplier.amountOwed}
                    </span>
                  </div>
                </div>
                <div className="flex items-center space-x-3" data-id="element-12239">
                  <button onClick={() => navigate(`/bill-form?supplierId=${supplier.id}`)} className="px-4 py-2 bg-[#FFC70A] hover:bg-[#D9A800] text-gray-900 text-sm font-medium rounded-lg transition-colors shadow-sm" data-id="element-12240">
                    {t('pages.SupplierDetail.add_bill')}
                  </button>
                  <button aria-label="More actions" className="p-2 border border-gray-200 rounded-lg text-gray-600 hover:bg-gray-50 transition-colors" data-id="element-12241">
                    <MoreHorizontalIcon size={20} aria-hidden="true" data-id="element-12242" />
                  </button>
                </div>
              </div>
            </div>

            {/* Details Section */}
            <div className="p-8 border-b border-gray-100" data-id="element-12243">
              <div className="flex justify-between items-center mb-6" data-id="element-12244">
                <h3 className="text-sm font-medium text-gray-500 uppercase tracking-wider" data-id="element-12245">
                  {t('pages.SupplierDetail.details')}
                </h3>
                <button onClick={() => navigate(`/supplier-form?id=${supplier.id}`)} className="text-sm font-medium text-[#B38A00] hover:text-[#8C6C00]" data-id="element-12246">
                  {t('pages.SupplierDetail.edit')}
                </button>
              </div>

              <div className="space-y-4" data-id="element-12247">
                {/* Type */}
                <div className="grid grid-cols-3 gap-4 border-b border-gray-50 pb-4" data-id="element-12248">
                  <div className="text-sm text-gray-500" data-id="element-12249">
                    {t('pages.BulkSupplierUpload.type')}
                  </div>
                  <div className="col-span-2" data-id="element-12250">
                    {supplier.supplierType === 'company' ? <span className="inline-flex items-center px-2.5 py-0.5 rounded-md text-xs font-medium bg-blue-50 text-blue-700 border border-blue-100" data-id="element-12251">
                        <BuildingIcon size={12} className="mr-1.5" data-id="element-12252" />
                        {t('pages.SupplierDetail.company')}
                      </span> : <span className="inline-flex items-center px-2.5 py-0.5 rounded-md text-xs font-medium bg-purple-50 text-purple-700 border border-purple-100" data-id="element-12253">
                        <UserIcon size={12} className="mr-1.5" data-id="element-12254" />
                        {t('pages.SupplierDetail.individual')}
                      </span>}
                  </div>
                </div>

                {/* Company-specific fields */}
                {supplier.supplierType === 'company' && <>
                    <div className="grid grid-cols-3 gap-4" data-id="element-12255">
                      <div className="text-sm text-gray-500" data-id="element-12256">
                        {t('pages.SupplierDetail.company_name_local')}
                      </div>
                      <div className="col-span-2 text-sm font-medium text-gray-900" data-id="element-12257">
                        {supplier.companyNameTh || '—'}
                      </div>
                    </div>
                    <div className="grid grid-cols-3 gap-4" data-id="element-12258">
                      <div className="text-sm text-gray-500" data-id="element-12259">
                        {t('pages.SupplierDetail.company_name_english')}
                      </div>
                      <div className="col-span-2 text-sm font-medium text-gray-900" data-id="element-12260">
                        {supplier.companyNameEn || '—'}
                      </div>
                    </div>
                    <div className="grid grid-cols-3 gap-4" data-id="element-12261">
                      <div className="text-sm text-gray-500" data-id="element-12262">
                        {t('pages.SupplierDetail.tax_id_registration_no')}
                      </div>
                      <div className="col-span-2 text-sm font-medium text-gray-900 font-mono" data-id="element-12263">
                        {supplier.taxId || '—'}
                      </div>
                    </div>
                    <div className="grid grid-cols-3 gap-4" data-id="element-12264">
                      <div className="text-sm text-gray-500" data-id="element-12265">
                        {t('pages.SupplierDetail.branch')}
                      </div>
                      <div className="col-span-2 text-sm font-medium text-gray-900" data-id="element-12266">
                        {supplier.branch === '00000' ? '00000 (Head Office)' : supplier.branch || '—'}
                      </div>
                    </div>
                    <div className="grid grid-cols-3 gap-4" data-id="element-12267">
                      <div className="text-sm text-gray-500" data-id="element-12268">
                        {t('pages.SupplierDetail.vat_registered')}
                      </div>
                      <div className="col-span-2" data-id="element-12269">
                        <span className={`inline-flex items-center px-2 py-0.5 rounded text-xs font-medium ${supplier.vatRegistered ? 'bg-green-50 text-green-700' : 'bg-gray-100 text-gray-600'}`} data-id="element-12270">
                          {supplier.vatRegistered ? 'Yes' : 'No'}
                        </span>
                      </div>
                    </div>
                    {supplier.contactPerson && <div className="grid grid-cols-3 gap-4" data-id="element-12271">
                        <div className="text-sm text-gray-500" data-id="element-12272">
                          {t('pages.SupplierDetail.contact_person')}
                        </div>
                        <div className="col-span-2 text-sm font-medium text-gray-900" data-id="element-12273">
                          {supplier.contactPerson}
                        </div>
                      </div>}
                  </>}

                {/* Individual-specific fields */}
                {supplier.supplierType === 'individual' && <>
                    <div className="grid grid-cols-3 gap-4" data-id="element-12274">
                      <div className="text-sm text-gray-500" data-id="element-12275">
                        {t('pages.SupplierDetail.full_name_thai')}
                      </div>
                      <div className="col-span-2 text-sm font-medium text-gray-900" data-id="element-12276">
                        {supplier.fullNameTh || '—'}
                      </div>
                    </div>
                    <div className="grid grid-cols-3 gap-4" data-id="element-12277">
                      <div className="text-sm text-gray-500" data-id="element-12278">
                        {t('pages.SupplierDetail.full_name_english')}
                      </div>
                      <div className="col-span-2 text-sm font-medium text-gray-900" data-id="element-12279">
                        {supplier.fullNameEn || '—'}
                      </div>
                    </div>
                    <div className="grid grid-cols-3 gap-4" data-id="element-12280">
                      <div className="text-sm text-gray-500" data-id="element-12281">
                        {t('pages.SupplierDetail.id_type')}
                      </div>
                      <div className="col-span-2 text-sm font-medium text-gray-900 capitalize" data-id="element-12282">
                        {supplier.idType === 'citizen_id' ? 'Citizen ID' : supplier.idType === 'passport' ? 'Passport' : '—'}
                      </div>
                    </div>
                    <div className="grid grid-cols-3 gap-4" data-id="element-12283">
                      <div className="text-sm text-gray-500" data-id="element-12284">
                        {t('pages.SupplierDetail.id_number')}
                      </div>
                      <div className="col-span-2 text-sm font-medium text-gray-900 font-mono" data-id="element-12285">
                        {supplier.idNumber || '—'}
                      </div>
                    </div>
                    {supplier.individualTaxId && <div className="grid grid-cols-3 gap-4" data-id="element-12286">
                        <div className="text-sm text-gray-500" data-id="element-12287">
                          {t('pages.BulkSupplierUpload.tax_id')}
                        </div>
                        <div className="col-span-2 text-sm font-medium text-gray-900 font-mono" data-id="element-12288">
                          {supplier.individualTaxId}
                        </div>
                      </div>}
                  </>}

                {/* Shared fields */}
                <div className="grid grid-cols-3 gap-4 border-t border-gray-50 pt-4" data-id="element-12289">
                  <div className="text-sm text-gray-500" data-id="element-12290">
                    {t('pages.BulkSupplierUpload.email')}
                  </div>
                  <div className="col-span-2 text-sm font-medium text-gray-900" data-id="element-12291">
                    {supplier.email}
                  </div>
                </div>
                {supplier.phone && <div className="grid grid-cols-3 gap-4" data-id="element-12292">
                    <div className="text-sm text-gray-500" data-id="element-12293">
                      {t('pages.BulkSupplierUpload.phone')}
                    </div>
                    <div className="col-span-2 text-sm font-medium text-gray-900" data-id="element-12294">
                      {supplier.phone}
                    </div>
                  </div>}
                <div className="grid grid-cols-3 gap-4" data-id="element-12295">
                  <div className="text-sm text-gray-500" data-id="element-12296">
                    {t('pages.SupplierDetail.billing_email')}
                  </div>
                  <div className="col-span-2 text-sm font-medium text-gray-900" data-id="element-12297">
                    {billingEmail}
                  </div>
                </div>
                <div className="grid grid-cols-3 gap-4" data-id="element-12298">
                  <div className="text-sm text-gray-500" data-id="element-12299">
                    {t('pages.SupplierDetail.address')}
                  </div>
                  <div className="col-span-2 text-sm font-medium text-gray-900" data-id="element-12300">
                    {fullAddress || '—'}
                  </div>
                </div>
                {supplier.internalReference && <div className="grid grid-cols-3 gap-4" data-id="element-12301">
                    <div className="text-sm text-gray-500" data-id="element-12302">
                      {t('pages.SupplierDetail.internal_reference')}
                    </div>
                    <div className="col-span-2 text-sm font-medium text-gray-900" data-id="element-12303">
                      {supplier.internalReference}
                    </div>
                  </div>}
              </div>
            </div>

            {/* Pay-in Method Section */}
            <div className="p-8 border-b border-gray-100" data-id="element-12304">
              <h3 className="text-sm font-medium text-gray-500 uppercase tracking-wider mb-6" data-id="element-12305">
                {t('pages.SupplierDetail.payin_method')}
              </h3>

              <div className="bg-gray-50 rounded-lg p-5 border border-gray-200" data-id="element-12306">
                <div className="flex items-center" data-id="element-12307">
                  <div className={`w-10 h-10 rounded-full flex items-center justify-center mr-4 ${supplier.payInMethod === 'acquiring' ? 'bg-green-100' : 'bg-blue-100'}`} data-id="element-12308">
                    {supplier.payInMethod === 'acquiring' ? <WalletIcon size={20} className="text-green-700" data-id="element-12309" /> : <CreditCardIcon size={20} className="text-blue-700" data-id="element-12310" />}
                  </div>
                  <div data-id="element-12311">
                    <div className="text-sm font-semibold text-gray-900" data-id="element-12312">
                      {supplier.payInMethod === 'acquiring' ? 'Acquiring Volume' : 'Card Payment'}
                    </div>
                    <div className="text-xs text-gray-500" data-id="element-12313">
                      {supplier.payInMethod === 'acquiring' ? 'Funded from merchant acquiring settlement balance' : 'Charged to linked card'}
                    </div>
                  </div>
                </div>

                {supplier.payInMethod === 'card' && supplier.cardNumber && <div className="grid grid-cols-2 gap-y-3 gap-x-6 mt-4 pt-4 border-t border-gray-200" data-id="element-12314">
                    <div data-id="element-12315">
                      <div className="text-xs text-gray-500 mb-1" data-id="element-12316">
                        {t('pages.SupplierDetail.cardholder')}
                      </div>
                      <div className="text-sm font-medium text-gray-900" data-id="element-12317">
                        {supplier.cardholderName}
                      </div>
                    </div>
                    <div data-id="element-12318">
                      <div className="text-xs text-gray-500 mb-1" data-id="element-12319">
                        {t('pages.SupplierDetail.card_number')}
                      </div>
                      <div className="text-sm font-medium text-gray-900 font-mono" data-id="element-12320">
                        {supplier.cardNumber}
                      </div>
                    </div>
                  </div>}
              </div>
            </div>

            {/* Payout Method Section */}
            <div className="p-8 border-b border-gray-100" data-id="element-12321">
              <div className="flex justify-between items-center mb-6" data-id="element-12322">
                <h3 className="text-sm font-medium text-gray-500 uppercase tracking-wider" data-id="element-12323">
                  {t('pages.SupplierDetail.payout_method')}
                </h3>
                <button onClick={() => navigate(`/supplier-form?id=${supplier.id}`)} className="text-sm font-medium text-[#B38A00] hover:text-[#8C6C00]" data-id="element-12324">
                  {t('pages.SupplierDetail.edit_payout_method')}
                </button>
              </div>

              <div className="bg-gray-50 rounded-lg p-5 border border-gray-200" data-id="element-12325">
                {supplier.payoutMethod === 'bank_account' ? <>
                    <div className="flex items-center mb-4" data-id="element-12326">
                      <div className="w-10 h-10 rounded-full bg-green-100 flex items-center justify-center mr-4" data-id="element-12327">
                        <LandmarkIcon size={20} className="text-green-700" data-id="element-12328" />
                      </div>
                      <div data-id="element-12329">
                        <div className="text-sm font-semibold text-gray-900" data-id="element-12330">
                          {t('pages.SupplierDetail.bank_account')}
                        </div>
                        <div className="text-xs text-gray-500" data-id="element-12331">
                          {t('pages.SupplierDetail.direct_transfer')}
                        </div>
                      </div>
                    </div>
                    <div className="grid grid-cols-2 gap-y-3 gap-x-6 mt-4 pt-4 border-t border-gray-200" data-id="element-12332">
                      <div data-id="element-12333">
                        <div className="text-xs text-gray-500 mb-1" data-id="element-12334">
                          {t('pages.SupplierDetail.bank_name')}
                        </div>
                        <div className="text-sm font-medium text-gray-900" data-id="element-12335">
                          {supplier.bankName}
                        </div>
                      </div>
                      <div data-id="element-12336">
                        <div className="text-xs text-gray-500 mb-1" data-id="element-12337">
                          {t('pages.SupplierDetail.account_number')}
                        </div>
                        <div className="text-sm font-medium text-gray-900 font-mono" data-id="element-12338">
                          {'***' + supplier.accountNumber.slice(-4)}
                        </div>
                      </div>
                      <div data-id="element-12339">
                        <div className="text-xs text-gray-500 mb-1" data-id="element-12340">
                          {t('pages.SupplierDetail.account_name')}
                        </div>
                        <div className="text-sm font-medium text-gray-900" data-id="element-12341">
                          {supplier.accountName}
                        </div>
                      </div>
                    </div>
                  </> : <>
                    <div className="flex items-center mb-4" data-id="element-12342">
                      <div className="w-10 h-10 rounded-full bg-purple-100 flex items-center justify-center mr-4" data-id="element-12343">
                        <SmartphoneIcon size={20} className="text-purple-700" data-id="element-12344" />
                      </div>
                      <div data-id="element-12345">
                        <div className="text-sm font-semibold text-gray-900" data-id="element-12346">
                          {t('pages.SupplierDetail.promptpay')}
                        </div>
                        <div className="text-xs text-gray-500" data-id="element-12347">
                          {supplier.promptPayType === 'phone' ? 'Phone number' : supplier.promptPayType === 'citizen_id' ? 'Citizen ID' : 'Tax ID'}
                        </div>
                      </div>
                    </div>
                    <div className="grid grid-cols-2 gap-y-3 gap-x-6 mt-4 pt-4 border-t border-gray-200" data-id="element-12348">
                      <div data-id="element-12349">
                        <div className="text-xs text-gray-500 mb-1" data-id="element-12350">
                          {t('pages.SupplierDetail.promptpay_id')}
                        </div>
                        <div className="text-sm font-medium text-gray-900 font-mono" data-id="element-12351">
                          {'***' + supplier.promptPayId.slice(-4)}
                        </div>
                      </div>
                      <div data-id="element-12352">
                        <div className="text-xs text-gray-500 mb-1" data-id="element-12353">
                          {t('pages.SupplierDetail.display_name')}
                        </div>
                        <div className="text-sm font-medium text-gray-900" data-id="element-12354">
                          {supplier.promptPayName}
                        </div>
                      </div>
                    </div>
                  </>}
              </div>
            </div>

            {/* Source Section */}
            <div className="p-8 border-b border-gray-100" data-id="element-12355">
              <h3 className="text-sm font-medium text-gray-500 uppercase tracking-wider mb-6" data-id="element-12356">
                {t('pages.SupplierDetail.source')}
              </h3>
              <div className="space-y-4" data-id="element-12357">
                <div className="grid grid-cols-3 gap-4" data-id="element-12358">
                  <div className="text-sm text-gray-500" data-id="element-12359">
                    {t('pages.SupplierDetail.platform')}
                  </div>
                  <div className="col-span-2 text-sm font-medium text-gray-900" data-id="element-12360">
                    {supplier.source}
                  </div>
                </div>
                <div className="grid grid-cols-3 gap-4" data-id="element-12361">
                  <div className="text-sm text-gray-500" data-id="element-12362">
                    {t('pages.SupplierDetail.date_created')}
                  </div>
                  <div className="col-span-2 text-sm font-medium text-gray-900" data-id="element-12363">
                    {supplier.dateCreated}
                  </div>
                </div>
              </div>
            </div>

            {/* Bills Section */}
            <div className="p-8" data-id="element-12364">
              <h3 className="text-sm font-medium text-gray-500 uppercase tracking-wider mb-4" data-id="element-12365">
                {t('pages.SupplierDetail.bills')}
              </h3>
              <div className="border border-gray-200 rounded-lg p-4 flex items-center justify-between cursor-pointer hover:bg-gray-50 transition-colors group" data-id="element-12366">
                <div data-id="element-12367">
                  <div className="text-sm font-medium text-gray-900 mb-1" data-id="element-12368">
                    {bills.total} {t('pages.SupplierDetail.bill')}
                    {bills.total !== 1 ? 's' : ''}{' '}
                    {t('pages.SupplierDetail.for')} {supplier.name}
                  </div>
                  <div className="text-sm text-gray-500" data-id="element-12369">
                    {bills.due} {t('pages.SupplierDetail.due')}{' '}
                    {bills.awaitingApproval}{' '}
                    {t('pages.SupplierDetail.awaiting_approval')}{' '}
                    {bills.scheduled} {t('pages.SupplierDetail.scheduled_and')}{' '}
                    {bills.paid} {t('pages.SupplierDetail.paid')}
                  </div>
                </div>
                <ChevronRightIcon size={20} className="text-gray-400 group-hover:text-gray-600 transition-colors" data-id="element-12370" />
              </div>
            </div>
          </div>
        </div>

        {/* Right Column - History */}
        <div className="lg:col-span-1" data-id="element-12371">
          <h3 className="text-xs font-medium text-gray-500 uppercase tracking-wider mb-4" data-id="element-12372">
            {t('pages.SupplierDetail.history')}
          </h3>
          <div className="space-y-6" data-id="element-12373">
            {history.map((event, index) => <div key={event.id} className="relative flex gap-4" data-id="element-12374">
                {index !== history.length - 1 && <div className="absolute left-[9px] top-6 bottom-[-24px] w-px bg-gray-200" data-id="element-12375"></div>}
                <div className="relative z-10 flex-shrink-0 w-5 h-5 bg-gray-100 rounded flex items-center justify-center mt-0.5" data-id="element-12376">
                  <PlusIcon size={12} className="text-gray-600" data-id="element-12377" />
                </div>
                <div data-id="element-12378">
                  <p className="text-sm font-medium text-gray-900" data-id="element-12379">
                    {event.action}
                  </p>
                  <p className="text-sm text-gray-500 mt-1" data-id="element-12380">{event.date}</p>
                </div>
              </div>)}
          </div>
        </div>
      </div>
    </div>;
}